//@ts-nocheck
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  DatePicker,
  Grid,
  Input,
  Select,
  Table,
  Tooltip,
  Checkbox,
} from "antd";
import dayjs from "dayjs";
import {
  FiClipboard,
  FiSearch,
  FiFilter,
  FiChevronUp,
  FiClock,
  FiPackage,
  FiMapPin,
  FiTruck,
  FiXCircle,
  FiFileText,
  FiExternalLink,
  FiCheckCircle,
  FiRotateCcw,
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { getOrderTracking } from "../../utils/API";
import { extractOrderTrackingRows } from "../../utils/orderTrackingCache";
import CommonHeader from "../../Components/commonHeader";
import PageLogoutButton from "../../Components/PageLogoutButton";
import "./order-list.css";

const { useBreakpoint } = Grid;

const DATE_FMT = "MM/DD/YYYY";

// Open dropdown / calendar popups inside the filter field itself (not <body>),
// so they scroll with the page instead of floating over the header.
const popupParent = (node: HTMLElement) => node.parentElement || document.body;
// Rows revealed per scroll step (infinite scroll, no pagination).
const PAGE_SIZE = 25;
const MAX_SLOTS = 5;

// `match` is compared against the status with everything but letters stripped,
// so "At Location", "at_location" and "AtLocation" all hit the same card.
// First match wins, so keep "cancel" ahead of the looser ones.
const ORDER_STATUS_KEYS = [
  { key: "open", label: "Open", match: "open", icon: <FiClock />, tone: "amber" },
  { key: "cancelled", label: "Cancelled", match: "cancel", icon: <FiXCircle />, tone: "gray" },
  { key: "shipped", label: "Shipped", match: "ship", icon: <FiTruck />, tone: "green" },
  { key: "incoming", label: "Incoming", match: "incoming", icon: <FiPackage />, tone: "blue" },
  { key: "atlocation", label: "At Location", match: "atlocation", icon: <FiMapPin />, tone: "purple" },
  { key: "rma", label: "RMA / Returned", match: ["rma", "return"], icon: <FiRotateCcw />, tone: "red" },
];
const normStatus = (v: any) => String(v ?? "").toLowerCase().replace(/[^a-z]/g, "");

const hasKey = (s: string, match: string | string[]) => (Array.isArray(match) ? match : [match]).some((m) => s.includes(m));

const INVOICE_STATUSES = ["Not Requested", "Requested", "Sent", "Paid", "Refunded", "Cancelled"];
const SHIPPING_STATUSES = ["Not Shipped", "Shipped", "In Transit", "Delivered", "Returned", "Cancelled"];

const clean = (v: any) => String(v ?? "").trim();

// A tracking cell sometimes holds free text ("FedEx Tracking #8750...", two numbers
// separated by "/" or a space). Pull the actual UPS (1Z...) / numeric tracking numbers
// out of it; fall back to the raw text when none is recognised.
const parseTrackings = (raw: string) => {
  const text = clean(raw);
  if (!text) return [];
  const found = text.match(/1Z[0-9A-Z]{16}|\d{10,}/gi);
  return found ? [...new Set(found)] : [text];
};

// Map a raw order-tracking row (snake_case from the API) to what this page needs.
const mapRow = (item: any, index: number) => {
  const slots = [];
  for (let i = 1; i <= MAX_SLOTS; i++) {
    const sfx = i === 1 ? "" : i === 2 ? "2" : String(i);
    const modelKey = i === 1 ? "model" : `model_${i}`;
    const manu = clean(item?.[`manu${sfx}`]);
    const model = clean(item?.[modelKey]);
    const vendor = clean(item?.[`vendor${sfx}`]);
    const tracking = clean(item?.[i === 1 ? "tracking_no" : `tracking_no_${i}`]);
    const trackingList = parseTrackings(tracking);
    const qty = clean(item?.[i === 1 ? "qty" : `qty_${i}`]);
    if (manu || model || vendor || tracking) slots.push({ manu, model, vendor, tracking: trackingList.join(", "), trackingList, qty });
  }
  const trackings = slots.flatMap((s) => s.trackingList);
  const dateShipped = clean(item?.date_shipped) || clean(item?.date_shipped_2);
  const paid = clean(item?.paid).toLowerCase();
  const invoiceNo = clean(item?.ate_invoice_no);
  const invoiceDate = clean(item?.invoice_date);
  const invoiceRequestedAt = item?.invoice_requested_at || null;
  const poStatus = clean(item?.po_status);

  // Everything below follows the order's own status first, so an order only shows
  // invoice / shipping information that belongs to its status (a cancelled order
  // is not "Paid", an open order is not "In Transit").
  const statusKey = poStatus.toLowerCase().replace(/[^a-z]/g, "");
  const isCancelled = statusKey.includes("cancel") || paid.startsWith("cancel");
  const isReturned = !isCancelled && (statusKey.includes("rma") || statusKey.includes("return"));
  const isRefunded = paid.startsWith("refund");
  const notYetShipped = !isCancelled && !isReturned && /open|incoming|atlocation/.test(statusKey);

  let invoiceStatus = "Not Requested";
  if (isCancelled) invoiceStatus = "Cancelled";
  else if (isRefunded) invoiceStatus = "Refunded";
  else if (paid.startsWith("yes") || paid === "true" || paid === "paid") invoiceStatus = "Paid";
  else if (invoiceNo || invoiceDate) invoiceStatus = "Sent";
  else if (invoiceRequestedAt) invoiceStatus = "Requested";

  // No delivery data exists yet, so "Delivered" only comes from the PO status.
  let shippingStatus = "Not Shipped";
  if (isCancelled) shippingStatus = "Cancelled";
  else if (isReturned) shippingStatus = "Returned";
  else if (poStatus.toLowerCase().includes("deliver")) shippingStatus = "Delivered";
  else if (notYetShipped) shippingStatus = "Not Shipped";
  else if (dateShipped || trackings.length) shippingStatus = "In Transit";
  // The order status says shipped but no ship date / tracking was ever entered.
  else if (poStatus.toLowerCase().includes("ship") && !poStatus.toLowerCase().includes("partial")) shippingStatus = "Shipped";

  // Next action: the saved OTC action wins; otherwise suggest the next step
  // from the order status, then from where it is in its invoice / shipping flow.
  const savedAction = clean(item?.actions);
  let suggestedAction = "";
  if (isCancelled) suggestedAction = "Order cancelled - no action needed";
  else if (isReturned) suggestedAction = "Handle RMA / return";
  else if (statusKey.includes("incoming")) suggestedAction = "Wait for the shipment to arrive";
  else if (statusKey.includes("atlocation")) suggestedAction = "Ship the order to the customer";
  else if (statusKey.includes("open")) suggestedAction = "Place order and confirm shipping";
  else if (invoiceStatus === "Refunded") suggestedAction = "Refunded - no action needed";
  else if (invoiceStatus === "Paid") suggestedAction = "Order complete - no action needed";
  else if (invoiceStatus === "Sent") suggestedAction = "Follow up on invoice payment";
  else if (invoiceStatus === "Requested") suggestedAction = "Send invoice to customer";
  else if (shippingStatus === "Not Shipped") suggestedAction = "Place order and confirm shipping";
  else suggestedAction = "Request invoice from accounting";

  // Latest update: only the server's own timestamp counts; "—" when it has none.
  // (dayjs(undefined) is "now", so only parse when a value is actually present.)
  const rawUpdatedAt = item?.updated_at || item?.updatedAt;
  const updatedAt = rawUpdatedAt ? dayjs(rawUpdatedAt) : null;
  const latestUpdate = updatedAt && updatedAt.isValid() ? updatedAt.toISOString() : "";

  return {
    key: String(item?.id ?? item?.ot_id ?? item?.order_tracking_id ?? `row-${index}`),
    id: item?.id ?? item?.ot_id ?? item?.order_tracking_id,
    quoteId: clean(item?.quote_id),
    customer: clean(item?.customer),
    slots,
    models: slots.map((s) => s.model).filter(Boolean),
    manus: [...new Set(slots.map((s) => s.manu).filter(Boolean))],
    vendors: [...new Set(slots.map((s) => s.vendor).filter(Boolean))],
    trackings,
    po: clean(item?.po_number),
    salesOrder: clean(item?.sales_order),
    orderDate: clean(item?.date),
    status: poStatus,
    invoiceStatus,
    shippingStatus,
    // Invoice total when invoiced; otherwise the order's gross sale.
    amount: item?.invoice_total ?? item?.gross_sale ?? null,
    paymentTerms: clean(item?.wire),
    freight: item?.shipping_cost ?? null,
    otcComments: clean(item?.otc_comments),
    invoiceNo,
    invoiceDate,
    invoiceDueDate: clean(item?.invoice_due_date),
    invoiceRequestedAt,
    paid: invoiceStatus === "Paid" ? "Paid" : invoiceStatus === "Refunded" ? "Refunded" : invoiceStatus === "Cancelled" ? "Cancelled" : "Not paid",
    dateShipped,
    rep: clean(item?.rep),
    nextAction: savedAction || suggestedAction,
    latestUpdate,
    comments: clean(item?.comments),
  };
};

const fmtMoney = (v: any) => {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return "—";
  return `$ ${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const fmtDate = (v: any) => {
  if (!v) return "—";
  const d = dayjs(v);
  return d.isValid() ? d.format(DATE_FMT) : String(v);
};

const orDash = (v: any) => (v === null || v === undefined || v === "" ? "—" : v);

// Carrier isn't stored anywhere yet, so best-effort guess from the tracking format.
const guessCarrier = (tracking: string) => {
  const t = clean(tracking).replace(/\s/g, "");
  if (!t) return "";
  if (/^1Z[0-9A-Z]{16}$/i.test(t)) return "UPS";
  if (/^\d{12}$|^\d{15}$/.test(t)) return "FedEx";
  return "";
};

const trackingUrl = (carrier: string, tracking: string) => {
  const t = encodeURIComponent(clean(tracking));
  if (!t) return "";
  if (carrier === "UPS") return `https://www.ups.com/track?tracknum=${t}`;
  if (carrier === "FedEx") return `https://www.fedex.com/fedextrack/?trknbr=${t}`;
  return "";
};

const statusTone = (status: string) => {
  const s = clean(status).toLowerCase();
  if (!s) return "gray";
  if (s.includes("cancel")) return "gray";
  if (s.includes("rma") || s.includes("return")) return "red";
  if (s.includes("ship") || s.includes("deliver")) return "green";
  if (s.includes("incoming")) return "blue";
  if (s.replace(/[^a-z]/g, "").includes("atlocation")) return "purple";
  if (s.includes("open") || s.includes("pend")) return "amber";
  if (s.includes("hold") || s.includes("rma")) return "red";
  return "blue";
};

const invoiceTone = (s: string) =>
  ({ Paid: "purple", Sent: "green", Requested: "amber", Refunded: "red", Cancelled: "gray", "Not Requested": "gray" }[s] || "gray");
const shippingTone = (s: string) =>
  ({ Delivered: "purple", "In Transit": "blue", Shipped: "green", Returned: "red", Cancelled: "gray", "Not Shipped": "gray" }[s] || "gray");

const Badge: React.FC<{ tone: string; children: React.ReactNode }> = ({ tone, children }) => (
  <span className={`ol-badge ol-badge--${tone}`}>{children}</span>
);

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="ol-field">
    <span className="ol-field__label">{label}</span>
    <span className="ol-field__value">{children}</span>
  </div>
);

// Table text cell: one line with "...". Only when the text is actually cut off
// does hover show the full value in a tooltip (above the cell, flipping below
// when there is no room), same look as the OTC page.
const Cell: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [truncated, setTruncated] = useState(false);
  // Tooltip is capped at the width of the column the text sits in.
  const [colWidth, setColWidth] = useState<number | undefined>(undefined);
  return (
    <Tooltip
      title={truncated ? children : null}
      placement="top"
      mouseEnterDelay={0.05}
      overlayStyle={colWidth ? { maxWidth: colWidth } : undefined}
      overlayInnerStyle={{ wordBreak: "break-word" }}
    >
      <div
        ref={ref}
        className={`ol-ell${className ? ` ${className}` : ""}`}
        onMouseEnter={() => {
          const el = ref.current;
          setTruncated(!!el && el.scrollWidth > el.clientWidth + 1);
          const cell = el?.closest("td");
          if (cell) setColWidth(Math.round(cell.getBoundingClientRect().width));
        }}
      >
        {children}
      </div>
    </Tooltip>
  );
};

// Names that differ only by case ("CASA" / "Casa") are one option.
const uniqueOptions = (values: string[]) => {
  const byKey = new Map<string, string>();
  values.filter(Boolean).forEach((v) => {
    const k = v.toLowerCase();
    if (!byKey.has(k)) byKey.set(k, v);
  });
  return [...byKey.values()].sort((a, b) => a.localeCompare(b)).map((v) => ({ value: v, label: v }));
};

const EMPTY_FILTERS = {
  search: "",
  dateRange: null,
  customer: undefined,
  status: undefined,
  invoiceStatus: undefined,
  shippingStatus: undefined,
  model: undefined,
  manufacturer: undefined,
  vendor: undefined,
  tracking: "",
  quoteId: "",
};

const OrderList: React.FC = () => {
  const navigate = useNavigate();
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const [draft, setDraft] = useState<any>(EMPTY_FILTERS);
  const [applied, setApplied] = useState<any>(EMPTY_FILTERS);
  const [sortDir, setSortDir] = useState<"descend" | "ascend">("descend");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [expandedKey, setExpandedKey] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const response = await getOrderTracking({ page: 1, limit: 10000 });
      setRows(extractOrderTrackingRows(response).map(mapRow));
    } catch {
      setRows([]);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const options = useMemo(
    () => ({
      customer: uniqueOptions(rows.map((r) => r.customer)),
      status: uniqueOptions(rows.map((r) => r.status)),
      model: uniqueOptions(rows.flatMap((r) => r.models)),
      manufacturer: uniqueOptions(rows.flatMap((r) => r.manus)),
      vendor: uniqueOptions(rows.flatMap((r) => r.vendors)),
    }),
    [rows],
  );

  const filtered = useMemo(() => {
    const f = applied;
    const q = clean(f.search).toLowerCase();
    const trk = clean(f.tracking).toLowerCase();
    const qid = clean(f.quoteId).toLowerCase();
    const [from, to] = f.dateRange || [];

    const list = rows.filter((r) => {
      if (q) {
        const hay = [r.quoteId, r.customer, r.po, r.salesOrder, ...r.models, ...r.manus, ...r.trackings]
          .join(" ")
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (from && to) {
        const d = dayjs(r.orderDate);
        if (!d.isValid() || d.isBefore(from.startOf("day")) || d.isAfter(to.endOf("day"))) return false;
      }
      if (f.customer && r.customer.toLowerCase() !== f.customer.toLowerCase()) return false;
      if (f.status && r.status.toLowerCase() !== f.status.toLowerCase()) return false;
      if (f.invoiceStatus && r.invoiceStatus !== f.invoiceStatus) return false;
      if (f.shippingStatus && r.shippingStatus !== f.shippingStatus) return false;
      if (f.model && !r.models.some((m) => m.toLowerCase() === f.model.toLowerCase())) return false;
      if (f.manufacturer && !r.manus.some((m) => m.toLowerCase() === f.manufacturer.toLowerCase())) return false;
      if (f.vendor && !r.vendors.some((m) => m.toLowerCase() === f.vendor.toLowerCase())) return false;
      if (trk && !r.trackings.some((t) => t.toLowerCase().includes(trk))) return false;
      if (qid && !r.quoteId.toLowerCase().includes(qid)) return false;
      return true;
    });

    const dir = sortDir === "ascend" ? 1 : -1;
    return [...list].sort((a, b) => {
      const da = dayjs(a.orderDate).valueOf() || 0;
      const db = dayjs(b.orderDate).valueOf() || 0;
      return (da - db) * dir;
    });
  }, [rows, applied, sortDir]);

  // Counts come from the rows already loaded — no separate summary endpoint.
  const counts = useMemo(() => {
    const base: Record<string, number> = {};
    ORDER_STATUS_KEYS.forEach((s) => (base[s.key] = 0));
    base.nostatus = 0;
    rows.forEach((r) => {
      const s = normStatus(r.status);
      const hit = ORDER_STATUS_KEYS.find((k) => hasKey(s, k.match));
      if (hit) base[hit.key] += 1;
      else base.nostatus += 1;
    });
    return base;
  }, [rows]);

  const total = filtered.length;
  const pageRows = useMemo(() => filtered.slice(0, visibleCount), [filtered, visibleCount]);
  const hasMore = visibleCount < total;

  // Start from the top again whenever the result set or sort order changes.
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [filtered]);

  // Reveal the next batch only on a real user scroll near the bottom. Driven by
  // the scroll event (not an observer) so the list can never load itself.
  useEffect(() => {
    const box = document.querySelector(".ol-scroll");
    if (!box || !hasMore) return;
    const onScroll = () => {
      if (box.scrollTop + box.clientHeight >= box.scrollHeight - 400) {
        setVisibleCount((c) => c + PAGE_SIZE);
      }
    };
    box.addEventListener("scroll", onScroll, { passive: true });
    return () => box.removeEventListener("scroll", onScroll);
  }, [hasMore, loading]);

  const applyFilters = () => {
    setApplied(draft);
    setExpandedKey(null);
  };
  const clearFilters = () => {
    setDraft(EMPTY_FILTERS);
    setApplied(EMPTY_FILTERS);
    setExpandedKey(null);
  };
  const setField = (k: string, v: any) => setDraft((d: any) => ({ ...d, [k]: v }));

  const toggleExpand = (key: string) => setExpandedKey((cur) => (cur === key ? null : key));
  const toggleSelect = (key: string, checked: boolean) =>
    setSelectedKeys((cur) => (checked ? [...new Set([...cur, key])] : cur.filter((k) => k !== key)));

  const renderDetail = (row: any) => {
    const mainTracking = row.trackings[0] || "";
    const carrier = guessCarrier(mainTracking);
    const link = trackingUrl(carrier, mainTracking);
    return (
      <div className="ol-detail">
        <div className="ol-detail__head">
          <div className="ol-detail__title">
            <strong>Quote ID: {orDash(row.quoteId)}</strong>
            <span>
              Customer: <b>{orDash(row.customer)}</b>
            </span>
            <span>
              Model: <b>{orDash(row.models.join(", "))}</b>
            </span>
            <span>
              Manufacturer: <b>{orDash(row.manus.join(", "))}</b>
            </span>
            <span>
              Order Date: <b>{fmtDate(row.orderDate)}</b>
            </span>
          </div>
          <button type="button" className="ol-collapse" onClick={() => setExpandedKey(null)}>
            Collapse <FiChevronUp />
          </button>
        </div>

        {row.slots.length > 1 && (
          <div className="ol-card" style={{ marginBottom: 12 }}>
            <h4 className="ol-card__title ol-card__title--blue">
              <FiPackage /> Line Items ({row.slots.length})
            </h4>
            {row.slots.map((sl: any, i: number) => (
              <Field key={i} label={`${i + 1}. ${orDash(sl.manu)} — ${orDash(sl.model)}`}>
                Qty {orDash(sl.qty)} · Vendor {orDash(sl.vendor)} · Tracking {orDash(sl.tracking)}
              </Field>
            ))}
          </div>
        )}

        <div className="ol-detail__grid">
          <section className="ol-card">
            <h4 className="ol-card__title ol-card__title--blue">
              <FiFileText /> Order Details
            </h4>
            <Field label="PO #">{orDash(row.po)}</Field>
            <Field label="Sales Order #">{orDash(row.salesOrder)}</Field>
            <Field label="Order Amount">{fmtMoney(row.amount)}</Field>
            <Field label="Payment Terms">{orDash(row.paymentTerms)}</Field>
            <Field label="Currency">$</Field>
          </section>

          <section className="ol-card">
            <h4 className="ol-card__title ol-card__title--green">
              <FiFileText /> Invoice Information
            </h4>
            <Field label="Invoice Requested">
              {row.invoiceRequestedAt ? fmtDate(row.invoiceRequestedAt) : "—"}
            </Field>
            <Field label="Invoice Sent">{["Sent", "Paid", "Refunded"].includes(row.invoiceStatus) || (row.invoiceStatus === "Cancelled" && (row.invoiceNo || row.invoiceDate)) ? "Sent" : "Not sent"}</Field>
            <Field label="Invoice #">{orDash(row.invoiceNo)}</Field>
            <Field label="Invoice Date">{fmtDate(row.invoiceDate)}</Field>
            <Field label="Due Date">{fmtDate(row.invoiceDueDate)}</Field>
            <Field label="Payment Status">{row.paid}</Field>
          </section>

          <section className="ol-card">
            <h4 className="ol-card__title ol-card__title--purple">
              <FiTruck /> Shipping &amp; Freight
            </h4>
            <Field label="Carrier">{orDash(carrier)}</Field>
            <Field label="Tracking #">
              {row.trackings.length
                ? row.trackings.map((t: string) => {
                    const l = trackingUrl(guessCarrier(t), t);
                    return l ? (
                      <a key={t} href={l} target="_blank" rel="noreferrer" className="ol-link" style={{ display: "block" }}>
                        {t} <FiExternalLink />
                      </a>
                    ) : (
                      <span key={t} style={{ display: "block" }}>{t}</span>
                    );
                  })
                : "—"}
            </Field>
            <Field label="Delivery Status">
              <Badge tone={shippingTone(row.shippingStatus)}>{row.shippingStatus}</Badge>
            </Field>
            <Field label="Date Shipped">{fmtDate(row.dateShipped)}</Field>
            <Field label="Freight Cost">{fmtMoney(row.freight)}</Field>
          </section>

          <section className="ol-card">
            <h4 className="ol-card__title ol-card__title--green">
              <FiCheckCircle /> Next Action
            </h4>
            <Field label="Assigned To">{orDash(row.rep)}</Field>
            <Field label="Next Action">{orDash(row.nextAction)}</Field>
            {(row.otcComments || row.comments) && (
              <div className="ol-notes" title="Notes">
                {[row.otcComments, row.comments].filter(Boolean).join("\n")}
              </div>
            )}
          </section>
        </div>
      </div>
    );
  };

  // No horizontal scroll: columns share the available width (fixed layout) and
  // long text is cut with "..." (full value on hover). The lower-priority
  // columns drop out on narrower screens; their data is still in the detail panel.
  // No horizontal scroll: columns share the available width (fixed layout).
  // Long text is cut with "..." and opens in place on hover (see Cell).
  const columns = [
    { title: "Quote ID", dataIndex: "quoteId", width: 90, render: (v: string) => <Cell className="ol-quote">{orDash(v)}</Cell> },
    { title: "Customer", dataIndex: "customer", width: 190, render: (v: string) => <Cell>{orDash(v)}</Cell> },
    { title: "Model", dataIndex: "models", width: 120, render: (m: string[]) => <Cell>{m.length ? (m.length > 1 ? `${m[0]} +${m.length - 1}` : m[0]) : "—"}</Cell> },
    { title: "Manufacturer", dataIndex: "manus", width: 130, responsive: ["xl"], render: (m: string[]) => <Cell>{orDash(m[0])}</Cell> },
    { title: "PO #", dataIndex: "po", width: 100, responsive: ["xxl"], render: (v: string) => <Cell>{orDash(v)}</Cell> },
    { title: "Sales Order #", dataIndex: "salesOrder", width: 115, responsive: ["xxl"], render: (v: string) => <Cell>{orDash(v)}</Cell> },
    {
      title: "Order Date",
      dataIndex: "orderDate",
      width: 115,
      sorter: true,
      sortOrder: sortDir,
      sortDirections: ["descend", "ascend"],
      render: fmtDate,
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 105,
      render: (s: string) => (s ? <Badge tone={statusTone(s)}>{s}</Badge> : "—"),
    },
    {
      title: "Invoice Status",
      dataIndex: "invoiceStatus",
      width: 130,
      render: (s: string) => <Badge tone={invoiceTone(s)}>{s}</Badge>,
    },
    {
      title: "Shipping Status",
      dataIndex: "shippingStatus",
      width: 125,
      render: (s: string) => <Badge tone={shippingTone(s)}>{s}</Badge>,
    },
    {
      title: "Tracking #",
      dataIndex: "trackings",
      width: 130,
      render: (t: string[]) => <Cell>{t.length ? (t.length > 1 ? `${t[0]} +${t.length - 1}` : t[0]) : "—"}</Cell>,
    },
    { title: "Total Amount", dataIndex: "amount", width: 120, align: "right", render: fmtMoney },
  ];

  const filterBar = (
    <div className="ol-panel ol-filters">
      <div className="ol-filters__grid">
        <div className="ol-ctl">
          <label>Date Range</label>
          <DatePicker.RangePicker
            getPopupContainer={popupParent}
            value={draft.dateRange}
            onChange={(v) => setField("dateRange", v)}
            format={DATE_FMT}
            className="ol-input"
            allowClear
          />
        </div>
        <div className="ol-ctl">
          <label>Customer</label>
          <Select
            getPopupContainer={popupParent}
            showSearch
            allowClear
            placeholder="Select customer..."
            value={draft.customer}
            options={options.customer}
            onChange={(v) => setField("customer", v)}
            className="ol-input"
          />
        </div>
        <div className="ol-ctl">
          <label>Order Status</label>
          <Select
            getPopupContainer={popupParent}
            allowClear
            placeholder="All Statuses"
            value={draft.status}
            options={options.status}
            onChange={(v) => setField("status", v)}
            className="ol-input"
          />
        </div>
        <div className="ol-ctl">
          <label>Invoice Status</label>
          <Select
            getPopupContainer={popupParent}
            allowClear
            placeholder="All Invoice Status"
            value={draft.invoiceStatus}
            options={INVOICE_STATUSES.map((v) => ({ value: v, label: v }))}
            onChange={(v) => setField("invoiceStatus", v)}
            className="ol-input"
          />
        </div>
        <div className="ol-ctl">
          <label>Shipping Status</label>
          <Select
            getPopupContainer={popupParent}
            allowClear
            placeholder="All Shipping Status"
            value={draft.shippingStatus}
            options={SHIPPING_STATUSES.map((v) => ({ value: v, label: v }))}
            onChange={(v) => setField("shippingStatus", v)}
            className="ol-input"
          />
        </div>
        <div className="ol-ctl">
          <label>Model</label>
          <Select
            getPopupContainer={popupParent}
            showSearch
            allowClear
            placeholder="Enter model..."
            value={draft.model}
            options={options.model}
            onChange={(v) => setField("model", v)}
            className="ol-input"
          />
        </div>
        <div className="ol-ctl">
          <label>Manufacturer</label>
          <Select
            getPopupContainer={popupParent}
            showSearch
            allowClear
            placeholder="Select manufacturer..."
            value={draft.manufacturer}
            options={options.manufacturer}
            onChange={(v) => setField("manufacturer", v)}
            className="ol-input"
          />
        </div>
        <div className="ol-ctl">
          <label>Vendor</label>
          <Select
            getPopupContainer={popupParent}
            showSearch
            allowClear
            placeholder="Select vendor..."
            value={draft.vendor}
            options={options.vendor}
            onChange={(v) => setField("vendor", v)}
            className="ol-input"
          />
        </div>
        <div className="ol-ctl">
          <label>Tracking #</label>
          <Input
            allowClear
            placeholder="Enter tracking #..."
            value={draft.tracking}
            onChange={(e) => setField("tracking", e.target.value)}
            onPressEnter={applyFilters}
            className="ol-input"
          />
        </div>
        <div className="ol-ctl">
          <label>Quote ID</label>
          <Input
            allowClear
            placeholder="Enter quote ID..."
            value={draft.quoteId}
            onChange={(e) => setField("quoteId", e.target.value)}
            onPressEnter={applyFilters}
            className="ol-input"
          />
        </div>
        <div className="ol-filters__btns">
          <Button type="primary" icon={<FiFilter />} onClick={applyFilters} className="ol-btn-apply">
            Apply Filters
          </Button>
          <Button onClick={clearFilters} className="ol-btn-clear">
            Clear
          </Button>
        </div>
      </div>
    </div>
  );

  const summary = (
    <div className="ol-panel ol-summary">
      <div className="ol-stat">
        <span className="ol-stat__icon ol-tone-blue">
          <FiClipboard />
        </span>
        <div>
          <div className="ol-stat__label">All</div>
          <div className="ol-stat__value">{rows.length}</div>
        </div>
      </div>
      {ORDER_STATUS_KEYS.map((s) => (
        <div className="ol-stat" key={s.key}>
          <span className={`ol-stat__icon ol-tone-${s.tone}`}>{s.icon}</span>
          <div>
            <div className="ol-stat__label">{s.label}</div>
            <div className="ol-stat__value">{counts[s.key]}</div>
          </div>
        </div>
      ))}
      {counts.nostatus > 0 && (
        <div className="ol-stat">
          <span className="ol-stat__icon ol-tone-gray">
            <FiClipboard />
          </span>
          <div>
            <div className="ol-stat__label">No Status</div>
            <div className="ol-stat__value">{counts.nostatus}</div>
          </div>
        </div>
      )}
    </div>
  );

  const footer = (
    <>
      <div className="ol-footer">
        <span className="ol-footer__text">
          Showing {pageRows.length} of {total} orders{hasMore ? " — scroll for more" : ""}
        </span>
      </div>
    </>
  );

  const mobileList = (
    <div className="ol-cards">
      {pageRows.map((row) => (
        <div key={row.key} className={`ol-order ${selectedKeys.includes(row.key) ? "is-selected" : ""}`}>
          <div className="ol-order__body" onClick={() => toggleExpand(row.key)}>
          <div className="ol-order__top">
            <span onClick={(e) => e.stopPropagation()}>
              <Checkbox checked={selectedKeys.includes(row.key)} onChange={(e) => toggleSelect(row.key, e.target.checked)} />
            </span>
            <span className="ol-quote">{orDash(row.quoteId)}</span>
            {row.status && <Badge tone={statusTone(row.status)}>{row.status}</Badge>}
            <span className="ol-order__amount">{fmtMoney(row.amount)}</span>
          </div>
          <div className="ol-order__cust">{orDash(row.customer)}</div>
          <div className="ol-order__meta">
            <Field label="Model">{orDash(row.models[0])}</Field>
            <Field label="Manufacturer">{orDash(row.manus[0])}</Field>
            <Field label="PO #">{orDash(row.po)}</Field>
            <Field label="Sales Order #">{orDash(row.salesOrder)}</Field>
            <Field label="Order Date">{fmtDate(row.orderDate)}</Field>
            <Field label="Tracking #">{orDash(row.trackings[0])}</Field>
          </div>
          <div className="ol-order__badges">
            <Badge tone={invoiceTone(row.invoiceStatus)}>{row.invoiceStatus}</Badge>
            <Badge tone={shippingTone(row.shippingStatus)}>{row.shippingStatus}</Badge>
          </div>
          </div>
          {expandedKey === row.key && renderDetail(row)}
        </div>
      ))}
    </div>
  );

  return (
    <div className="ol-shell">
      <div className="ol-header">
        <CommonHeader className="mb-0" recordInputLabel="Records" rightContent={<PageLogoutButton />} />
      </div>
      <div className="ol-scroll">
        <div className="ol-wrap">
          <div className="ol-top">
            <div className="ol-top__title">
              <span className="ol-top__icon">
                <FiClipboard />
              </span>
              <div>
                <h1>Order Desk</h1>
                <p>View and manage all orders in one place. Track status, check evidence and take action.</p>
              </div>
            </div>
            <div className="ol-top__search">
              <Input
                allowClear
                prefix={<FiSearch />}
                placeholder="Search by quote ID, customer, model..."
                value={draft.search}
                onChange={(e) => setField("search", e.target.value)}
                onPressEnter={applyFilters}
              />
              <Button type="primary" onClick={applyFilters}>
                Search
              </Button>
            </div>
          </div>

          {filterBar}
          {summary}

          <div className="ol-panel ol-table">
            {error ? (
              <div className="ol-empty">
                Could not load orders.{" "}
                <Button type="link" onClick={load}>
                  Retry
                </Button>
              </div>
            ) : isMobile ? (
              loading ? (
                <div className="ol-empty">Loading orders…</div>
              ) : pageRows.length ? (
                mobileList
              ) : (
                <div className="ol-empty">No orders match these filters.</div>
              )
            ) : (
              <Table
                size="middle"
                loading={loading}
                columns={columns}
                dataSource={pageRows}
                rowKey="key"
                pagination={false}
                tableLayout="fixed"
                rowClassName={(r) =>
                  `ol-row-click ${selectedKeys.includes(r.key) ? "ol-row-selected" : ""} ${expandedKey === r.key ? "ol-row-open" : ""}`
                }
                onRow={(r) => ({
                  onClick: (e: any) => {
                    // Ignore clicks on the checkbox / links inside the row.
                    if (e.target.closest(".ant-checkbox-wrapper, a, button")) return;
                    toggleExpand(r.key);
                  },
                })}
                rowSelection={{
                  selectedRowKeys: selectedKeys,
                  onChange: (keys) => setSelectedKeys(keys as string[]),
                }}
                onChange={(_p, _f, sorter: any) => {
                  if (sorter?.order) setSortDir(sorter.order);
                  else setSortDir((d) => (d === "descend" ? "ascend" : "descend"));
                }}
                expandable={{
                  expandedRowKeys: expandedKey ? [expandedKey] : [],
                  expandedRowRender: renderDetail,
                  showExpandColumn: false,
                }}
                locale={{ emptyText: "No orders match these filters." }}
              />
            )}
            {footer}
          </div>
        </div>
      </div>

    </div>
  );
};

export default OrderList;
